// src/pages/markets/board/ScreenerBoardView.tsx

import { Box, Snackbar, Typography } from '@mui/material';
import { BoardColumn } from './board/BoardColumn';
import { SharedFiltersDialog } from './board/SharedFiltersDialog';
import { BOARD_COLUMNS } from './board/columnConfig';
import { BoardGroupsContext } from './state/boardGroupsContext';
import { CardSkeleton } from './card/CardSkeleton';
import { ProfileBar } from './board/ProfileBar';
import {
    DeleteProfileDialog,
    PromoteProfileDialog,
} from './board/ProfileDialogs';
import { useBoardState } from './state/useBoardState';
import { PageFrame } from '../../../components/common/page/PageFrame';
import { ColumnsHost } from './phone/ColumnsHost';
import { useIsPhone } from './phone/useIsPhone';
import {
    PHONE_PROFILE_ROW_HEIGHT,
    PhoneBoardSkeleton,
} from './phone/PhoneBoardSkeleton';

const FOOTER_LINKS_SPACE = 4;
const PROFILE_ROW_HEIGHT = 26;

const gridSx = {
    flex: 1,
    minHeight: 0,
    display: 'grid',
    gridTemplateRows: '1fr',
    gridTemplateColumns: {
        xs: 'repeat(2, minmax(300px, 1fr))',
        lg: 'repeat(4, minmax(300px, 1fr))',
    },
    gap: 2,
    overflowX: 'auto',
    overflowY: 'hidden',
} as const;

// On a phone the page frame has no side padding (the pager runs edge to edge),
// no gap and no top padding: the tabs and the control rows set their own.
// px is responsive like the frame's own, or its media query would win.
const PHONE_FRAME_SX = { px: { xs: 0 }, pt: 0, pb: 1, gap: 0 } as const;

export function ScreenerBoardView() {
    const phone = useIsPhone();
    const board = useBoardState();
    const { schema, filters, layout, now, profileBar } = board;

    if (board.error && !schema) {
        return (
            <PageFrame fill sx={{ pb: FOOTER_LINKS_SPACE }}>
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '13px', color: 'error.main' }}>
                        The board schema did not load. Reload the page to try again.
                    </Typography>
                </Box>
            </PageFrame>
        );
    }

    if (!board.ready || !schema || !filters || !layout) {
        // The phone's skeleton has the loaded board's shape, tabs and all.
        if (phone) {
            return (
                <PageFrame fill sx={PHONE_FRAME_SX}>
                    <PhoneBoardSkeleton tabCount={BOARD_COLUMNS.length} />
                </PageFrame>
            );
        }
        return (
            <PageFrame fill sx={{ pb: FOOTER_LINKS_SPACE }}>
                <Box sx={{ height: PROFILE_ROW_HEIGHT, flexShrink: 0 }} />
                <Box sx={gridSx}>
                    {BOARD_COLUMNS.map(c => (
                        <Box
                            key={c.id}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.75,
                                minHeight: 0,
                                overflow: 'hidden',
                            }}
                        >
                            <CardSkeleton />
                            <CardSkeleton />
                        </Box>
                    ))}
                </Box>
            </PageFrame>
        );
    }

    return (
        <BoardGroupsContext.Provider value={board.boardGroups}>
            <PageFrame fill sx={phone ? PHONE_FRAME_SX : { pb: FOOTER_LINKS_SPACE }}>
                <Box sx={{ display: 'flex', minHeight: PROFILE_ROW_HEIGHT, flexShrink: 0, ...(phone && { px: 2, py: '4px', minHeight: PHONE_PROFILE_ROW_HEIGHT, boxSizing: 'border-box' }) }}>
                    <ProfileBar {...profileBar.barProps} touch={phone} />
                </Box>

                <ColumnsHost phone={phone} configs={board.configs} gridSx={gridSx}>
                    {board.configs.map(config => (
                        <BoardColumn
                            key={config.id}
                            config={config}
                            schema={schema}
                            filters={filters[config.id]}
                            defaults={board.defaultsBySlot[config.id]}
                            bodyMode={layout[config.id].body}
                            cardView={layout[config.id].view}
                            now={now}
                            onFiltersChange={patch =>
                                board.handleFiltersChange(config.id, patch)
                            }
                            onReset={() => board.handleReset(config.id)}
                            onShare={() => board.handleShare(config.id)}
                            onBodyModeChange={mode =>
                                board.handleLayoutChange(config.id, { body: mode })
                            }
                            onCardViewChange={view =>
                                board.handleLayoutChange(config.id, { view })
                            }
                            onCategoryChange={category =>
                                board.handleCategoryChange(config.id, category)
                            }
                            phone={phone}
                        />
                    ))}
                </ColumnsHost>
            </PageFrame>

            <SharedFiltersDialog
                open={board.dialogOpen}
                columns={board.sharedForDialog}
                onSave={board.handleSaveShared}
                onDismiss={() => board.setDialogOpen(false)}
            />

            <DeleteProfileDialog
                profile={profileBar.pendingDelete}
                onCancel={profileBar.cancelDelete}
                onConfirm={profileBar.confirmDelete}
            />

            <PromoteProfileDialog
                profile={profileBar.pendingPromote}
                shared={profileBar.shared}
                onCancel={profileBar.cancelPromote}
                onConfirm={profileBar.confirmPromote}
            />

            <Snackbar
                open={board.toast !== null}
                autoHideDuration={3000}
                onClose={() => board.setToast(null)}
                message={board.toast ?? ''}
            />
        </BoardGroupsContext.Provider>
    );
}
